import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, Button, Stack, SxProps, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { ReactNode } from "react";
import { DefaultValues, FieldValues, FormProvider, useForm, UseFormReturn } from "react-hook-form";

import { SubRecordRow } from "./useSubRecordEditor";

/**
 * Presentational pieces of the shared repeatable child-list editor used by §13
 * episodic waste lines, §14 consolidated sites, and §16 HSM activities on the
 * Filing Notifications step: the grid (with a shared Edit/Remove actions column
 * and delete confirmation) and the isolated-draft add/edit dialog. The
 * open/commit state machine lives in `useSubRecordEditor`. Each section supplies
 * its own row type, columns, and dialog fields; these keep the three sections
 * visually and behaviorally consistent.
 */

/**
 * Renders the list header (label + Add button), the DataGrid (with a shared
 * Edit/Remove actions column), and the delete-confirmation dialog. The caller
 * passes only its data columns; the actions column is appended here so every
 * section gets the same affordances (and a delete confirmation).
 */
export function SubRecordGrid<T extends object>({
  rows,
  dataColumns,
  listLabel,
  addLabel,
  isDisplayOnly,
  onAdd,
  onEdit,
  rowToDelete,
  onRequestDelete,
  onConfirmDelete,
  deleteHeader,
  renderDeleteLabel,
}: {
  rows: SubRecordRow<T>[];
  dataColumns: GridColDef<SubRecordRow<T>>[];
  listLabel: string;
  addLabel: string;
  isDisplayOnly: boolean;
  onAdd: () => void;
  onEdit: (index: number) => void;
  rowToDelete: SubRecordRow<T> | null;
  onRequestDelete: (row: SubRecordRow<T> | null) => void;
  onConfirmDelete: () => void;
  deleteHeader: string;
  renderDeleteLabel: (row: SubRecordRow<T>) => ReactNode;
}) {
  const columns: GridColDef<SubRecordRow<T>>[] = [
    ...dataColumns,
    ...(isDisplayOnly
      ? []
      : [
          {
            field: "actions",
            type: "actions" as const,
            align: "right" as const,
            width: 100,
            getActions: ({ row }: { row: SubRecordRow<T> }) => [
              <GridActionsCellItem
                key="edit"
                label="Edit"
                icon={<Edit />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row.id);
                }}
              />,
              <GridActionsCellItem
                key="remove"
                label="Remove"
                icon={<Delete />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestDelete(row);
                }}
              />,
            ],
          },
        ]),
  ];

  return (
    <Box sx={{ gridColumn: "span 12", mt: 1 }}>
      <Stack
        direction="row"
        justifyContent={rows.length > 0 ? "space-between" : "flex-end"}
        alignItems="center"
        spacing={2}
        mb={1}
      >
        {rows.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {listLabel}
          </Typography>
        )}
        {!isDisplayOnly && (
          <Button startIcon={<Add />} size="small" onClick={onAdd}>
            {addLabel}
          </Button>
        )}
      </Stack>
      {rows.length > 0 && (
        <DataGrid<SubRecordRow<T>>
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          onRowClick={({ row }) => onEdit(row.id)}
        />
      )}
      {rowToDelete && (
        <ConfirmDialog
          open
          onClose={() => onRequestDelete(null)}
          onConfirm={onConfirmDelete}
          header={deleteHeader}
        >
          {/* The header already names what's being removed, so this is just the
              row's own label with no field label. */}
          <Typography variant="body2" sx={{ pb: 3 }}>
            {renderDeleteLabel(rowToDelete)}
          </Typography>
        </ConfirmDialog>
      )}
    </Box>
  );
}

/**
 * Add/edit dialog for a single sub-record. Edits an ISOLATED draft form seeded
 * from `initialValues`; nothing is written back to the parent form — and no
 * parent validation runs — until the user clicks Save, at which point
 * `onCommit` receives the draft values (the section appends/updates its parent
 * field array). Cancel simply discards the draft. Fields are supplied via
 * `renderFields`, which receives the draft form so its inputs bind to it.
 */
export function SubRecordDialog<T extends FieldValues>({
  title,
  gridSx,
  initialValues,
  isDisplayOnly,
  onCancel,
  onCommit,
  renderFields,
}: {
  title: string;
  gridSx: SxProps;
  initialValues: DefaultValues<T>;
  isDisplayOnly: boolean;
  onCancel: () => void;
  onCommit: (values: T) => void;
  renderFields: (form: UseFormReturn<T>) => ReactNode;
}) {
  const form = useForm<T>({ defaultValues: initialValues });
  const handleSave = () => {
    void form.handleSubmit((values) => onCommit(values))();
  };

  return (
    <FormProvider {...form}>
      <Dialog
        open
        onClose={onCancel}
        title={title}
        maxWidth="md"
        fullWidth
        noPrimaryAction={isDisplayOnly}
        primaryActionButtonProps={{ onClick: handleSave }}
      >
        <Box sx={gridSx}>{renderFields(form)}</Box>
      </Dialog>
    </FormProvider>
  );
}
